import {useEffect, useMemo, useState} from 'react'
import {useLazyQuery, useMutation} from '@apollo/client'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {
  CREATE_ASSIGNMENT_RULE,
  DELETE_ASSIGNMENT_RULE,
  GET_ASSIGNMENT_RULES,
  RESET_ASSIGNMENT_CREDITS,
  UPDATE_ASSIGNMENT_RULE,
} from '../../../../gql/queries/assignmentRulesQuery'
import {GET_GROUPS_PIPELINE} from '../../../../gql/queries/pipelines'
import {GET_ALL_GROUPS} from '../../../../gql/queries/assignmentGroupQuery'
import {
  INodeAssignmentRule,
  IResAssignmentRules,
  IResCreateAssignmentRule,
  IResDeleteAssignmentRule,
  IResResetAssignmentCredits,
  IResUpdateAssignmentRule,
} from '../../../../types/assignmentRules'
import {IResGroups} from '../../../../types/assignmentGroups'
import {IPipelineGroupsPipeline} from '../../../../types/users'
import {parseId} from '../../../../helpers'
import {useDisclourse} from '../../../../hooks/useDisclourse'

interface IPipelineRulesTabProps {
  pipelineId: string
  pipelineName: string
}

const space = ' '
const percent = '%'
const colon = ':'
const capSeparator = ' / '

const rtM = defineMessages({
  title: {id: 'leads.pipelines.rules.title', defaultMessage: 'Assignment rules'},
  hint: {
    id: 'leads.pipelines.rules.hint',
    defaultMessage:
      'Each rule is a quota. With weights 1/1/1 every group receives a third of the leads, no matter how many users it has. Without rules the leads are split over a flat pool of users.',
  },
  empty: {
    id: 'leads.pipelines.rules.empty',
    defaultMessage:
      'No quotas configured. The leads of this pipeline are split over a flat pool, so each group receives in proportion to how many users it has.',
  },
  addRule: {id: 'leads.pipelines.rules.add', defaultMessage: 'Add quota'},
  editRule: {id: 'leads.pipelines.rules.edit', defaultMessage: 'Edit quota'},
  colGroup: {id: 'leads.pipelines.rules.colGroup', defaultMessage: 'Group'},
  colEligible: {id: 'leads.pipelines.rules.colEligible', defaultMessage: 'Restricted to'},
  colWeight: {id: 'leads.pipelines.rules.colWeight', defaultMessage: 'Weight'},
  colShare: {id: 'leads.pipelines.rules.colShare', defaultMessage: 'Share'},
  colReceives: {id: 'leads.pipelines.rules.colReceives', defaultMessage: 'Receive'},
  colToday: {id: 'leads.pipelines.rules.colToday', defaultMessage: 'Today'},
  colCap: {id: 'leads.pipelines.rules.colCap', defaultMessage: 'Daily cap'},
  colActions: {id: 'leads.pipelines.edit.colActions', defaultMessage: 'Actions'},
  fieldGroup: {
    id: 'leads.pipelines.rules.fieldGroup',
    defaultMessage: 'Group that receives the quota',
  },
  fieldEligible: {
    id: 'leads.pipelines.rules.fieldEligible',
    defaultMessage: 'Restrict to members of',
  },
  fieldEligibleHelp: {
    id: 'leads.pipelines.rules.fieldEligibleHelp',
    defaultMessage: 'Only users in BOTH groups receive leads. Leave empty to use the whole group.',
  },
  fieldWeightHelp: {
    id: 'leads.pipelines.rules.fieldWeightHelp',
    defaultMessage: 'Relative quota. Use 1 in every group for an even split. 0 disables it.',
  },
  fieldCapHelp: {
    id: 'leads.pipelines.rules.fieldCapHelp',
    defaultMessage: 'Max leads per day for this group. Empty means no cap.',
  },
  fieldEnabled: {id: 'leads.pipelines.rules.fieldEnabled', defaultMessage: 'Active'},
  fieldOrder: {id: 'leads.pipelines.rules.fieldOrder', defaultMessage: 'Order (tie-break)'},
  noneOption: {id: 'leads.pipelines.rules.noneOption', defaultMessage: '— Whole group —'},
  selectGroup: {id: 'leads.pipelines.rules.selectGroup', defaultMessage: 'Select a group'},
  notLinked: {
    id: 'leads.pipelines.rules.notLinked',
    defaultMessage: 'This group is not linked to the pipeline in the Groups tab',
  },
  resetCredits: {id: 'leads.pipelines.rules.resetCredits', defaultMessage: 'Reset rotation'},
  resetHint: {
    id: 'leads.pipelines.rules.resetHint',
    defaultMessage: 'Restarts the round robin from scratch. Use it after changing the weights.',
  },
  confirmDelete: {
    id: 'leads.pipelines.rules.confirmDelete',
    defaultMessage: 'Delete the quota for {group}?',
  },
  toastCreated: {id: 'leads.pipelines.rules.toast.created', defaultMessage: 'Quota created'},
  toastUpdated: {id: 'leads.pipelines.rules.toast.updated', defaultMessage: 'Quota updated'},
  toastDeleted: {id: 'leads.pipelines.rules.toast.deleted', defaultMessage: 'Quota deleted'},
  toastReset: {id: 'leads.pipelines.rules.toast.reset', defaultMessage: 'Rotation restarted'},
  noEligibleUsers: {
    id: 'leads.pipelines.rules.noEligibleUsers',
    defaultMessage: 'No active user matches this quota, so it will be skipped',
  },
})

export const PipelineRulesTab: React.FC<IPipelineRulesTabProps> = ({pipelineId, pipelineName}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage(
      {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
      {message}
    )

  // parseId devuelve string | number segun el formato del id; se normaliza a string.
  const numericPipelineId = String(parseId(pipelineId))

  const {isOpen: isOpenForm, onClose: onCloseForm, onOpen: onOpenForm} = useDisclourse()
  const [editing, setEditing] = useState<INodeAssignmentRule | null>(null)

  const [gqlGetRules, {data, loading, error, refetch}] = useLazyQuery<IResAssignmentRules>(
    GET_ASSIGNMENT_RULES,
    {fetchPolicy: 'no-cache'}
  )
  const [gqlAllGroups, {data: dataAllGroups}] = useLazyQuery<IResGroups>(GET_ALL_GROUPS, {
    fetchPolicy: 'no-cache',
  })
  const [gqlPipelineGroups, {data: dataPipelineGroups}] = useLazyQuery<IPipelineGroupsPipeline>(
    GET_GROUPS_PIPELINE,
    {fetchPolicy: 'no-cache'}
  )

  const [gqlDelete, {loading: loadingDelete}] =
    useMutation<IResDeleteAssignmentRule>(DELETE_ASSIGNMENT_RULE)
  const [gqlReset, {loading: loadingReset}] =
    useMutation<IResResetAssignmentCredits>(RESET_ASSIGNMENT_CREDITS)

  useEffect(() => {
    gqlGetRules({variables: {pipelineId: numericPipelineId}})
    gqlAllGroups()
    gqlPipelineGroups({variables: {id: numericPipelineId}})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericPipelineId])

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const rules = data?.pipelineAssignmentRules ?? []
  const allGroups = dataAllGroups?.allGroupAssignments?.edges ?? []

  /** Grupos vinculados al pipeline: se preseleccionan como restricción y se usan
   *  para avisar cuando una cuota apunta a un grupo que no está vinculado. */
  const pipelineGroups = useMemo(
    () => dataPipelineGroups?.pipelineById?.groups?.edges ?? [],
    [dataPipelineGroups]
  )
  const pipelineGroupIds = useMemo(
    () => new Set(pipelineGroups.map(({node}) => String(parseId(node.id)))),
    [pipelineGroups]
  )

  // El peso es relativo, así que el porcentaje se calcula sobre las cuotas activas.
  const totalWeight = rules
    .filter((r) => r.enabled && r.weight > 0)
    .reduce((acc, r) => acc + r.weight, 0)

  const share = (rule: INodeAssignmentRule) => {
    if (!rule.enabled || rule.weight <= 0 || totalWeight === 0) return 0
    return Math.round((rule.weight / totalWeight) * 100)
  }

  const handleDelete = (rule: INodeAssignmentRule) => {
    // eslint-disable-next-line no-restricted-globals
    const ok = window.confirm(intl.formatMessage(rtM.confirmDelete, {group: rule.group.name}))
    if (!ok) return
    gqlDelete({variables: {id: String(parseId(rule.id))}}).then((res) => {
      const payload = res.data?.deletePipelineAssignmentRule
      if (payload?.success) {
        toast.success(intl.formatMessage(rtM.toastDeleted))
        refetch()
      } else {
        toast.error(errMsg(payload?.errors?.[0]?.message))
      }
    })
  }

  const handleReset = () => {
    gqlReset({variables: {pipelineId: numericPipelineId}}).then((res) => {
      const payload = res.data?.resetPipelineAssignmentCredits
      if (payload?.success) {
        toast.success(intl.formatMessage(rtM.toastReset))
        refetch()
      } else {
        toast.error(errMsg(payload?.errors?.[0]?.message))
      }
    })
  }

  return (
    <div className='policy-form'>
      <div className='d-flex align-items-start justify-content-between gap-3 mb-4'>
        <div>
          <h5 className='mb-1 d-flex align-items-center gap-2'>
            {intl.formatMessage(rtM.title)}
            <span className='badge badge-light-primary'>{rules.length}</span>
          </h5>
          <p className='text-muted fs-8 mb-0'>{intl.formatMessage(rtM.hint)}</p>
        </div>
        <div className='d-flex align-items-center gap-2 flex-shrink-0'>
          {rules.length > 0 && (
            <Button
              size='sm'
              variant='outline-secondary'
              title={intl.formatMessage(rtM.resetHint)}
              disabled={loadingReset}
              onClick={handleReset}
            >
              {loadingReset ? (
                <Spinner size='sm' animation='border' />
              ) : (
                <i className='bi bi-arrow-repeat' />
              )}
              {space}
              {intl.formatMessage(rtM.resetCredits)}
            </Button>
          )}
          <Button
            size='sm'
            className='d-flex align-items-center gap-2'
            onClick={() => {
              setEditing(null)
              onOpenForm()
            }}
          >
            <i className='bi bi-plus fs-4' />
            {intl.formatMessage(rtM.addRule)}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className='text-muted py-5'>
          <Spinner size='sm' animation='border' className='me-2' />
        </div>
      ) : rules.length === 0 ? (
        <div className='text-muted text-center border border-dashed rounded py-6 px-4'>
          <i className='bi bi-exclamation-circle me-2' />
          {intl.formatMessage(rtM.empty)}
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-row-dashed align-middle gs-0 gy-2 mb-0'>
            <thead>
              <tr className='fw-bold text-muted fs-8 text-uppercase'>
                <th>{intl.formatMessage(rtM.colGroup)}</th>
                <th>{intl.formatMessage(rtM.colEligible)}</th>
                <th className='text-center'>{intl.formatMessage(rtM.colWeight)}</th>
                <th className='text-center'>{intl.formatMessage(rtM.colShare)}</th>
                <th className='text-center'>{intl.formatMessage(rtM.colReceives)}</th>
                <th className='text-center'>{intl.formatMessage(rtM.colToday)}</th>
                <th className='text-end'>{intl.formatMessage(rtM.colActions)}</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const notLinked = !pipelineGroupIds.has(String(parseId(rule.group.id)))
                return (
                  <tr key={rule.id} className={rule.enabled ? undefined : 'opacity-50'}>
                    <td>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='fw-semibold'>{rule.group.name}</span>
                        {!rule.enabled && (
                          <span className='badge badge-light-warning'>
                            <FormattedMessage
                              id='common.status.inactive'
                              defaultMessage='Inactive'
                            />
                          </span>
                        )}
                        {notLinked && (
                          <i
                            className='bi bi-exclamation-triangle text-warning'
                            title={intl.formatMessage(rtM.notLinked)}
                          />
                        )}
                      </div>
                      <span className='text-muted fs-8'>
                        {rule.group.userCount}
                        {space}
                        <FormattedMessage
                          id='leads.pipelines.rules.usersInGroup'
                          defaultMessage='users in group'
                        />
                      </span>
                    </td>
                    <td>
                      {rule.eligibleGroup ? (
                        <span>{rule.eligibleGroup.name}</span>
                      ) : (
                        <span className='text-muted'>{intl.formatMessage(rtM.noneOption)}</span>
                      )}
                    </td>
                    <td className='text-center fw-semibold'>{rule.weight}</td>
                    <td className='text-center'>
                      <span
                        className={`badge ${
                          share(rule) > 0 ? 'badge-light-primary' : 'badge-light-secondary'
                        }`}
                      >
                        {share(rule)}
                        {percent}
                      </span>
                    </td>
                    <td className='text-center'>
                      <span className={rule.eligibleUserCount === 0 ? 'text-danger' : undefined}>
                        {rule.eligibleUserCount}
                      </span>
                      {rule.eligibleUserCount === 0 && (
                        <i
                          className='bi bi-exclamation-circle text-danger ms-1'
                          title={intl.formatMessage(rtM.noEligibleUsers)}
                        />
                      )}
                    </td>
                    <td className='text-center'>
                      {rule.leadsToday}
                      {rule.dailyCap !== null && (
                        <span className='text-muted'>
                          {capSeparator}
                          {rule.dailyCap}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className='d-flex align-items-center justify-content-end gap-1'>
                        <button
                          className='bg-transparent border-0'
                          title={intl.formatMessage({
                            id: 'common.action.edit',
                            defaultMessage: 'Edit',
                          })}
                          onClick={() => {
                            setEditing(rule)
                            onOpenForm()
                          }}
                        >
                          <i className='bi bi-pencil text-primary fs-5' />
                        </button>
                        <button
                          className='bg-transparent border-0'
                          disabled={loadingDelete}
                          title={intl.formatMessage({
                            id: 'common.action.delete',
                            defaultMessage: 'Delete',
                          })}
                          onClick={() => handleDelete(rule)}
                        >
                          <i className='bi bi-trash text-danger fs-5' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {isOpenForm && (
        <ModalAssignmentRuleForm
          isOpen={isOpenForm}
          onClose={onCloseForm}
          refetch={refetch}
          pipelineId={numericPipelineId}
          pipelineName={pipelineName}
          editing={editing}
          allGroups={allGroups.map(({node}) => ({
            id: String(parseId(node.id)),
            name: node.name,
            userCount: node.user?.edges?.length,
          }))}
          pipelineGroups={pipelineGroups.map(({node}) => ({
            id: String(parseId(node.id)),
            name: node.name,
          }))}
          existingGroupIds={rules.map((r) => String(parseId(r.group.id)))}
        />
      )}
    </div>
  )
}

interface IGroupOption {
  id: string
  name: string
  userCount?: number
}

interface IFormProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  pipelineId: string
  pipelineName: string
  editing: INodeAssignmentRule | null
  allGroups: IGroupOption[]
  pipelineGroups: IGroupOption[]
  existingGroupIds: string[]
}

const ModalAssignmentRuleForm: React.FC<IFormProps> = ({
  isOpen,
  onClose,
  refetch,
  pipelineId,
  pipelineName,
  editing,
  allGroups,
  pipelineGroups,
  existingGroupIds,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage(
      {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
      {message}
    )

  const [groupId, setGroupId] = useState(editing ? String(parseId(editing.group.id)) : '')
  // Se preselecciona el único grupo vinculado al pipeline, que es el caso normal
  // (un grupo maestro que define quién de cada contact center trabaja el pipeline).
  const [eligibleGroupId, setEligibleGroupId] = useState(
    editing
      ? editing.eligibleGroup
        ? String(parseId(editing.eligibleGroup.id))
        : ''
      : pipelineGroups.length === 1
      ? pipelineGroups[0].id
      : ''
  )
  const [weight, setWeight] = useState(editing ? String(editing.weight) : '1')
  const [enabled, setEnabled] = useState(editing ? editing.enabled : true)
  const [dailyCap, setDailyCap] = useState(
    editing && editing.dailyCap !== null ? String(editing.dailyCap) : ''
  )
  const [order, setOrder] = useState(editing ? String(editing.order) : '')

  const [gqlCreate, {loading: loadingCreate}] =
    useMutation<IResCreateAssignmentRule>(CREATE_ASSIGNMENT_RULE)
  const [gqlUpdate, {loading: loadingUpdate}] =
    useMutation<IResUpdateAssignmentRule>(UPDATE_ASSIGNMENT_RULE)
  const saving = loadingCreate || loadingUpdate

  // En creación se ocultan los grupos que ya tienen cuota: el backend lo rechaza
  // por unique_together y es mejor no ofrecer una opción que va a fallar.
  const selectableGroups = editing
    ? allGroups
    : allGroups.filter((g) => !existingGroupIds.includes(g.id))

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!groupId) return

    const ruleData = {
      pipelineId,
      groupId,
      eligibleGroupId: eligibleGroupId || null,
      weight: Number(weight || 0),
      enabled,
      dailyCap: dailyCap === '' ? null : Number(dailyCap),
      order: order === '' ? 0 : Number(order),
    }

    const done = (success?: boolean, message?: string) => {
      if (success) {
        toast.success(intl.formatMessage(editing ? rtM.toastUpdated : rtM.toastCreated))
        refetch()
        onClose()
      } else {
        toast.error(errMsg(message))
      }
    }

    // Las dos mutaciones se llaman por separado en vez de unificarlas en una
    // promesa: sus payloads son tipos distintos y unirlos pierde el tipado.
    if (editing) {
      gqlUpdate({variables: {id: String(parseId(editing.id)), ruleData}}).then((res) => {
        const payload = res.data?.updatePipelineAssignmentRule
        done(payload?.success, payload?.errors?.[0]?.message)
      })
    } else {
      gqlCreate({variables: {ruleData}}).then((res) => {
        const payload = res.data?.createPipelineAssignmentRule
        done(payload?.success, payload?.errors?.[0]?.message)
      })
    }
  }

  const selectedGroup = allGroups.find((g) => g.id === groupId)

  return (
    <Modal show={isOpen} onHide={onClose} backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title className='fs-4'>
          {intl.formatMessage(editing ? rtM.editRule : rtM.addRule)}
          <span className='text-muted fs-7 d-block'>{pipelineName}</span>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className='mb-4'>
            <Form.Label>{intl.formatMessage(rtM.fieldGroup)}</Form.Label>
            <Form.Select value={groupId} required onChange={(e) => setGroupId(e.target.value)}>
              <option value=''>{intl.formatMessage(rtM.selectGroup)}</option>
              {selectableGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className='mb-4'>
            <Form.Label>{intl.formatMessage(rtM.fieldEligible)}</Form.Label>
            <Form.Select
              value={eligibleGroupId}
              onChange={(e) => setEligibleGroupId(e.target.value)}
            >
              <option value=''>{intl.formatMessage(rtM.noneOption)}</option>
              {allGroups
                .filter((g) => g.id !== groupId)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </Form.Select>
            <Form.Text className='text-muted'>
              {intl.formatMessage(rtM.fieldEligibleHelp)}
            </Form.Text>
          </Form.Group>

          <div className='d-flex gap-3'>
            <Form.Group className='mb-4 flex-fill'>
              <Form.Label>{intl.formatMessage(rtM.colWeight)}</Form.Label>
              <Form.Control
                type='number'
                min={0}
                value={weight}
                required
                onChange={(e) => setWeight(e.target.value)}
              />
              <Form.Text className='text-muted'>
                {intl.formatMessage(rtM.fieldWeightHelp)}
              </Form.Text>
            </Form.Group>
            <Form.Group className='mb-4 flex-fill'>
              <Form.Label>{intl.formatMessage(rtM.colCap)}</Form.Label>
              <Form.Control
                type='number'
                min={0}
                value={dailyCap}
                placeholder='—'
                onChange={(e) => setDailyCap(e.target.value)}
              />
              <Form.Text className='text-muted'>{intl.formatMessage(rtM.fieldCapHelp)}</Form.Text>
            </Form.Group>
          </div>

          <div className='d-flex align-items-center gap-5'>
            <Form.Group>
              <Form.Label>{intl.formatMessage(rtM.fieldOrder)}</Form.Label>
              <Form.Control
                type='number'
                min={0}
                value={order}
                placeholder='0'
                style={{maxWidth: '120px'}}
                onChange={(e) => setOrder(e.target.value)}
              />
            </Form.Group>
            <Form.Check
              type='switch'
              id='rule-enabled'
              className='mt-5'
              label={intl.formatMessage(rtM.fieldEnabled)}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </div>

          {selectedGroup?.userCount !== undefined && (
            <div className='text-muted fs-8 mt-3'>
              <i className='bi bi-info-circle me-1' />
              {selectedGroup.name}
              {colon}
              {space}
              {selectedGroup.userCount}
              {space}
              <FormattedMessage
                id='leads.pipelines.rules.usersInGroup'
                defaultMessage='users in group'
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center gap-2'
            disabled={saving}
          >
            {saving && <Spinner animation='border' size='sm' />}
            <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
